// React imports
import React, { useState } from 'react';

// Generic imports
import axios from 'axios';

// MUI imports
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLifeRing } from '@fortawesome/pro-light-svg-icons';

// Local imports
import { generateHelpContext } from './help-context.js';

function AiAgentPane(props) {
    const { organization, accesstoken, me, featureToggle, accounts, suppliers, opportunities } = props;
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isRunning, setIsRunning] = useState(false);
    const messagesEndRef = React.useRef(null);
    const theme = useTheme();

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    React.useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleKeyPress = async (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (isRunning) return;
            const trimmed = (inputValue || '').trim();
            if (!trimmed) return;

            const userMsg = { role: 'user', content: trimmed };
            const nextMessages = [...messages, userMsg];
            setMessages(nextMessages);
            setInputValue('');

            setIsRunning(true);
            try {
                setMessages(prev => [...prev, { role: 'assistant', content: '', isThinking: true }]);

                const context = generateHelpContext({ organization, me, featureToggle, accounts, suppliers, opportunities });
                const { data } = await axios.post(
                    `/api/help/chat?organizationId=${organization.id}&accesstoken=${accesstoken}`,
                    {
                        messages: nextMessages.filter(m => !m.isThinking).map(m => ({ role: m.role, content: m.content })),
                        context,
                    }
                );

                setMessages(prev => prev.filter(m => !m.isThinking).concat({
                    role: 'assistant',
                    content: data.content,
                }));
            } catch (e) {
                setMessages(prev => prev.filter(m => !m.isThinking).concat({
                    role: 'assistant',
                    content: 'Sorry, I encountered an error. Please try again or contact support at help@monada.ai.',
                }));
            } finally {
                setIsRunning(false);
            }
        }
    };

    function onContactSupport() {
        const conversationText = messages
            .filter(m => !m.isThinking)
            .map(m => `${m.role === 'user' ? 'User' : 'Help Agent'}: ${m.content.replace(/<[^>]*>/g, '')}`)
            .join('\n\n');
        const subject = encodeURIComponent(`Support request from ${me?.email} (${organization.name || organization.id})`);
        const body = encodeURIComponent(`Hi,\n\nI need help with the following:\n\n---\nConversation:\n${conversationText}\n---\n\nWorkspace: ${organization.name || organization.id}\nUser: ${me?.email}\n`);
        window.open(`mailto:help@monada.ai?subject=${subject}&body=${body}`, '_blank');
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
        }}>
            {/* Header */}
            <Box sx={{ position: 'sticky', top: 0, background: 'transparent', padding: '6px 24px', mb: 1, borderBottom: '1px solid rgba(59, 130, 246, 0.3)' }}>
                <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                    Monada AI Assistant <span style={{ fontSize: '9px', color: '#3b82f6', fontWeight: 700, marginLeft: '4px', padding: '1px 4px', border: '1px solid #3b82f6', borderRadius: '3px' }}>BETA</span>
                </Typography>
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 1 }}>
                {messages.length === 0 && (
                    <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                        <Typography variant='body2' sx={{ mb: 1, fontWeight: 600 }}>How can I help?</Typography>
                        <Typography variant='caption'>Ask me anything about how to use Monada — creating quotes, managing accounts, setting up targets, and more.</Typography>
                    </Box>
                )}
                {messages.map((message, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                            mb: 1.5,
                        }}
                    >
                        <Box sx={{
                            maxWidth: '85%',
                            p: 1.5,
                            bgcolor: message.role === 'user' ? 'primary.main' : theme.palette.panel1,
                            color: message.role === 'user' ? 'white' : 'text.primary',
                            borderRadius: '12px',
                            borderBottomRightRadius: message.role === 'user' ? '4px' : '12px',
                            borderBottomLeftRadius: message.role === 'user' ? '12px' : '4px',
                            fontSize: '13px',
                            lineHeight: 1.5,
                            '& p': { margin: '4px 0' },
                            '& ol, & ul': { margin: '4px 0', paddingLeft: '20px' },
                            '& li': { margin: '2px 0' },
                            '& b': { fontWeight: 600 },
                        }}>
                            {message.isThinking ? (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', py: 0.5 }}>
                                    {[0, 1, 2].map(i => (
                                        <Box key={i} sx={{
                                            width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.400',
                                            animation: 'pulse 1s infinite', animationDelay: `${i * 0.2}s`,
                                            '@keyframes pulse': { '0%': { opacity: 0.3 }, '50%': { opacity: 1 }, '100%': { opacity: 0.3 } },
                                        }} />
                                    ))}
                                </Box>
                            ) : message.role === 'assistant' ? (
                                <div dangerouslySetInnerHTML={{ __html: message.content }} />
                            ) : (
                                message.content
                            )}
                        </Box>
                    </Box>
                ))}
                <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.border1}` }}>
                <TextField
                    fullWidth
                    size='small'
                    placeholder='Ask a question...'
                    variant='outlined'
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isRunning}
                    sx={{ '& .MuiInputBase-root': { fontSize: '13px', borderRadius: '8px' } }}
                />
            </Box>

            {/* Support link */}
            <Box sx={{ px: 2, pb: 1.5, textAlign: 'center' }}>
                <Link
                    component='button'
                    variant='caption'
                    onClick={onContactSupport}
                    sx={{ fontSize: '11px', color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main' }, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                >
                    <FontAwesomeIcon icon={faLifeRing} style={{ fontSize: '10px' }} />
                    Can't find what you need? Contact support
                </Link>
            </Box>
        </Box>
    );
}

export default AiAgentPane;
